import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { format } from 'date-fns';
import { MemberAvailabilityBlock, CreateMemberAvailabilityRequest } from '../types/availability';

// UI-only state
interface AvailabilityUIState {
  calendar: {
    currentDate: Date;
    viewMode: 'week' | 'month' | 'day';
  };
  
  interaction: {
    mode: 'idle' | 'dragging' | 'creating' | 'editing' | 'deleting';
    selectedDate: Date | null;
    dragSelection: {
      dayIndex: number;
      startTimeIndex: number;
      endTimeIndex: number;
    } | null;
    editingBlock: MemberAvailabilityBlock | null;
  };
  
  modals: {
    create: {
      isOpen: boolean;
      prefilledData: Partial<CreateMemberAvailabilityRequest> | null;
    };
    edit: {
      isOpen: boolean;
      block: MemberAvailabilityBlock | null;
    };
    delete: {
      isOpen: boolean;
      blockId: number | null;
    };
  };
}

// UI-only actions
type AvailabilityUIAction =
  | { type: 'SET_CURRENT_DATE'; payload: Date }
  | { type: 'SET_VIEW_MODE'; payload: 'week' | 'month' | 'day' }
  | { type: 'START_DRAG'; payload: { dayIndex: number; timeIndex: number; date: Date } }
  | { type: 'UPDATE_DRAG'; payload: { endTimeIndex: number } }
  | { type: 'END_DRAG' }
  | { type: 'START_CREATE'; payload: { date: Date; prefilledData?: Partial<CreateMemberAvailabilityRequest> } }
  | { type: 'START_EDIT'; payload: MemberAvailabilityBlock }
  | { type: 'START_DELETE'; payload: number }
  | { type: 'RESET_INTERACTION' }
  | { type: 'OPEN_CREATE_MODAL'; payload?: Partial<CreateMemberAvailabilityRequest> }
  | { type: 'CLOSE_CREATE_MODAL' }
  | { type: 'OPEN_EDIT_MODAL'; payload: MemberAvailabilityBlock }
  | { type: 'CLOSE_EDIT_MODAL' }
  | { type: 'OPEN_DELETE_MODAL'; payload: number }
  | { type: 'CLOSE_DELETE_MODAL' }
  | { type: 'CLOSE_ALL_MODALS' };

const initialUIState: AvailabilityUIState = {
  calendar: {
    currentDate: new Date(),
    viewMode: 'week',
  },
  interaction: {
    mode: 'idle',
    selectedDate: null,
    dragSelection: null,
    editingBlock: null,
  },
  modals: {
    create: { isOpen: false, prefilledData: null },
    edit: { isOpen: false, block: null },
    delete: { isOpen: false, blockId: null },
  },
};

// Helper function for time calculation (30-min slots, midnight-to-midnight)
// slotIndex 0 = 00:00, 1 = 00:30, ..., 16 = 08:00, etc.
function getTimeFromSlotIndex(slotIndex: number): string {
  const hour = Math.floor(slotIndex / 2);
  const minute = (slotIndex % 2) * 30;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
}

function availabilityUIReducer(state: AvailabilityUIState, action: AvailabilityUIAction): AvailabilityUIState {
  switch (action.type) {
    case 'SET_CURRENT_DATE':
      return { ...state, calendar: { ...state.calendar, currentDate: action.payload } };
      
    case 'SET_VIEW_MODE':
      return { ...state, calendar: { ...state.calendar, viewMode: action.payload } };

    case 'START_DRAG':
      return {
        ...state,
        interaction: {
          mode: 'dragging',
          selectedDate: action.payload.date,
          dragSelection: {
            dayIndex: action.payload.dayIndex,
            startTimeIndex: action.payload.timeIndex,
            endTimeIndex: action.payload.timeIndex,
          },
          editingBlock: null,
        },
      };
      
    case 'UPDATE_DRAG':
      if (state.interaction.mode !== 'dragging' || !state.interaction.dragSelection) return state;
      return {
        ...state,
        interaction: {
          ...state.interaction,
          dragSelection: {
            ...state.interaction.dragSelection,
            endTimeIndex: action.payload.endTimeIndex,
          },
        },
      };
      
    case 'END_DRAG':
      return {
        ...state,
        interaction: { ...state.interaction, mode: 'idle' },
        modals: {
          ...state.modals,
          create: {
            isOpen: true,
            prefilledData: state.interaction.dragSelection ? {
              // Use LOCAL date parts (date-fns format) — NOT toISOString(), which
              // converts to UTC and shifts the calendar day by one for any tz
              // offset from a local-midnight Date (the drag-create off-by-a-day bug).
              date: state.interaction.selectedDate ? format(state.interaction.selectedDate, 'yyyy-MM-dd') : '',
              start_time: getTimeFromSlotIndex(Math.min(
                state.interaction.dragSelection.startTimeIndex,
                state.interaction.dragSelection.endTimeIndex
              )),
              end_time: getTimeFromSlotIndex(Math.max(
                state.interaction.dragSelection.startTimeIndex,
                state.interaction.dragSelection.endTimeIndex
              ) + 1),
              recurrence_type: 'one_time',
            } : null,
          },
        },
      };
      
    case 'START_CREATE':
      return {
        ...state,
        interaction: { mode: 'creating', selectedDate: action.payload.date, dragSelection: null, editingBlock: null },
        modals: {
          ...state.modals,
          create: {
            isOpen: true,
            prefilledData: action.payload.prefilledData || null,
          },
        },
      };
      
    case 'START_EDIT':
      return {
        ...state,
        interaction: { mode: 'editing', selectedDate: null, dragSelection: null, editingBlock: action.payload },
        modals: {
          ...state.modals,
          edit: {
            isOpen: true,
            block: action.payload,
          },
        },
      };
      
    case 'START_DELETE':
      return {
        ...state,
        interaction: { mode: 'deleting', selectedDate: null, dragSelection: null, editingBlock: null },
        modals: {
          ...state.modals,
          delete: {
            isOpen: true,
            blockId: action.payload,
          },
        },
      };
      
    case 'RESET_INTERACTION':
      return {
        ...state,
        interaction: { mode: 'idle', selectedDate: null, dragSelection: null, editingBlock: null },
      };

    case 'OPEN_CREATE_MODAL':
      return {
        ...state,
        modals: {
          ...state.modals,
          create: { isOpen: true, prefilledData: action.payload || null },
        },
      };
      
    case 'CLOSE_CREATE_MODAL':
      return {
        ...state,
        interaction: { ...state.interaction, mode: 'idle' },
        modals: { ...state.modals, create: { isOpen: false, prefilledData: null } },
      };
      
    case 'OPEN_EDIT_MODAL':
      return {
        ...state,
        modals: {
          ...state.modals,
          edit: { isOpen: true, block: action.payload },
        },
      };
      
    case 'CLOSE_EDIT_MODAL':
      return {
        ...state,
        interaction: { ...state.interaction, mode: 'idle' },
        modals: { ...state.modals, edit: { isOpen: false, block: null } },
      };
      
    case 'OPEN_DELETE_MODAL':
      return {
        ...state,
        modals: {
          ...state.modals,
          delete: { isOpen: true, blockId: action.payload },
        },
      };
      
    case 'CLOSE_DELETE_MODAL':
      return {
        ...state,
        interaction: { ...state.interaction, mode: 'idle' },
        modals: { ...state.modals, delete: { isOpen: false, blockId: null } },
      };
      
    case 'CLOSE_ALL_MODALS':
      return {
        ...state,
        interaction: { ...state.interaction, mode: 'idle' },
        modals: {
          create: { isOpen: false, prefilledData: null },
          edit: { isOpen: false, block: null },
          delete: { isOpen: false, blockId: null },
        },
      };

    default:
      return state;
  }
}

interface AvailabilityUIContextType {
  state: AvailabilityUIState;
  dispatch: React.Dispatch<AvailabilityUIAction>;
}

const AvailabilityUIContext = createContext<AvailabilityUIContextType | undefined>(undefined);

interface AvailabilityUIProviderProps {
  children: ReactNode;
}

export const AvailabilityUIProvider: React.FC<AvailabilityUIProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(availabilityUIReducer, initialUIState);

  return (
    <AvailabilityUIContext.Provider value={{ state, dispatch }}>
      {children}
    </AvailabilityUIContext.Provider>
  );
};

export const useAvailabilityUI = () => {
  const context = useContext(AvailabilityUIContext);
  if (context === undefined) {
    throw new Error('useAvailabilityUI must be used within an AvailabilityUIProvider');
  }
  return context;
};

export type { AvailabilityUIState, AvailabilityUIAction };